import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useI18n } from "@/i18n";
import { useRouter, type Route } from "@/router/Router";
import { useCart } from "@/state/CartContext";
import { useCatalog } from "@/state/CatalogContext";
import { useConfig } from "@/lib/useConfig";
import { useCompare } from "@/lib/compare";
import { formatUzs } from "@/lib/format";
import { RichText } from "@/components/RichText";
import { DeliveryPicker } from "@/components/DeliveryPicker";
import {
  ApiError,
  bookEvent,
  cancelBooking,
  claimGift,
  faqFeedback,
  faqViewed,
  fetchEvents,
  fetchFaq,
  fetchGift,
  fetchLatestReviews,
  fetchMyBookings,
  fetchRecipe,
  fetchRecipes,
  fetchReviews,
  fetchSubscription,
  fetchWishlist,
  mediaUrl,
  type EventBooking,
  type FaqCategory,
  type FaqItem,
  type GiftView,
  type Recipe,
  type ShopEvent,
} from "@/lib/api";
import { getTelegramUser, openExternalLink } from "@/lib/telegram";
import { DI, DProductCard, DSkeletonCard, PATH, WI, dsvg, flyToCart, reviewsText } from "@/components/desktop/desktopUi";
import type { Product, Review } from "@/types";

/* ───────────────────────── shared bits ───────────────────────── */

/** Page header of the secondary desktop pages: crumbs, eyebrow, big serif title, lead. */
function Head({ crumbs, eyebrow, title, lead, children }: { crumbs: Array<[string, Route?]>; eyebrow?: string; title: string; lead?: string; children?: React.ReactNode }) {
  const { t } = useI18n();
  const { navigate } = useRouter();
  return (
    <section className="dk-wrap dx-head">
      <div className="dc-crumbs">
        <button onClick={() => navigate({ name: "home" })}>{t("desktop.crumbHome")}</button>
        {crumbs.map(([label, to], i) => (
          <React.Fragment key={i}>
            {" › "}
            {to ? <button onClick={() => navigate(to)}>{label}</button> : <span>{label}</span>}
          </React.Fragment>
        ))}
      </div>
      {eyebrow && <div className="dk-eyebrow dx-head__eb">{eyebrow}</div>}
      <h1 className="dk-serif">{title}</h1>
      {lead && <p>{lead}</p>}
      {children}
    </section>
  );
}

/** Calm empty / error block with an optional action. */
function Empty({ title, hint, action }: { title: string; hint?: string; action?: React.ReactNode }) {
  return (
    <div className="dx-empty">
      <span className="dx-empty__ico">{WI(PATH.bean, 54)}</span>
      <h3 className="dk-serif">{title}</h3>
      {hint && <p>{hint}</p>}
      {action}
    </div>
  );
}

const when = (iso: string, locale: string) =>
  new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "ru-RU", { weekday: "short", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Tashkent" }).format(new Date(iso));
const dayMonth = (iso: string, locale: string) => {
  const d = new Date(iso);
  const f = (o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "ru-RU", { ...o, timeZone: "Asia/Tashkent" }).format(d);
  return { day: f({ day: "numeric" }), month: f({ month: "short" }).replace(".", "") };
};

const ICO = {
  search: dsvg('<circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/>', 20, 1.7),
  plus: dsvg('<path d="M12 5v14M5 12h14"/>', 20, 1.6),
  minus: dsvg('<path d="M5 12h14"/>', 20, 1.6),
  chat: dsvg('<path d="M4 5h16v11H9l-5 4z"/>', 18, 1.7),
  tg: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M21.5 4.2 2.9 11.4c-1 .4-1 1.6.1 1.9l4.6 1.5 1.8 5.6c.2.8 1.2 1 1.8.4l2.6-2.4 4.8 3.5c.7.5 1.7.1 1.9-.8l3.2-15.2c.2-1-.8-1.8-1.7-1.5z" /></svg>,
  clock: dsvg('<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/>', 16, 1.7),
  pin: dsvg('<path d="M12 21s-6-5.7-6-11a6 6 0 0 1 12 0c0 5.3-6 11-6 11z"/><circle cx="12" cy="10" r="2.2"/>', 16, 1.7),
  drop: dsvg('<path d="M12 3c4 4 6 7 6 10a6 6 0 0 1-12 0c0-3 2-6 6-10z"/>', 16, 1.7),
  temp: dsvg('<path d="M10 14V5a2 2 0 0 1 4 0v9a4 4 0 1 1-4 0z"/>', 16, 1.7),
  grind: dsvg('<circle cx="12" cy="12" r="2"/><circle cx="6" cy="8" r="1.4"/><circle cx="18" cy="8" r="1.4"/><circle cx="7" cy="17" r="1.4"/><circle cx="17" cy="17" r="1.4"/>', 16, 1.7),
  bean: dsvg(PATH.bean, 16, 1.7),
  ticket: dsvg('<path d="M3 8a2 2 0 0 0 2-2h14a2 2 0 0 0 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 0-2 2H5a2 2 0 0 0-2-2v-2a2 2 0 0 0 0-4z"/><path d="M14 6v12" stroke-dasharray="2 2"/>', 18, 1.6),
  gift: dsvg('<path d="M4 11h16v9H4zM3 7h18v4H3zM12 7v13M12 7c-1.5-3-5-3.5-5-1.2C7 7 9.5 7 12 7zM12 7c1.5-3 5-3.5 5-1.2C17 7 14.5 7 12 7z"/>', 26, 1.5),
};

/* ───────────────────────── FAQ ───────────────────────── */

const FAQ_CATS: FaqCategory[] = ["order", "delivery", "returns", "coffee", "business", "other"];

export function DesktopFaq({ category }: { category?: string }) {
  const { t, locale } = useI18n();
  const { botUsername } = useConfig();
  const [items, setItems] = useState<FaqItem[] | null>(null);
  const [cat, setCat] = useState<FaqCategory | "all">((FAQ_CATS as string[]).includes(category ?? "") ? (category as FaqCategory) : "all");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [voted, setVoted] = useState<Record<string, boolean>>({});
  useEffect(() => {
    fetchFaq().then(setItems).catch(() => setItems([]));
  }, [locale]);
  const present = useMemo(() => FAQ_CATS.filter((c) => items?.some((i) => i.category === c)), [items]);
  const q = query.trim().toLowerCase();
  const shown = useMemo(() => (items ?? []).filter((i) => (cat === "all" || q || i.category === cat) && (!q || i.question.toLowerCase().includes(q) || i.answer.toLowerCase().includes(q))), [items, cat, q]);
  const toggle = (id: string) => setOpen((cur) => {
    if (cur !== id) faqViewed(id);
    return cur === id ? null : id;
  });
  const vote = (id: string, ok: boolean) => {
    setVoted((v) => ({ ...v, [id]: ok }));
    faqFeedback(id, ok).catch(() => {});
  };

  return (
    <div className="dx">
      <Head crumbs={[[t("faq.title")]]} eyebrow={t("dx.faqEyebrow")} title={t("faq.title")} lead={t("dx.faqLead")}>
        <label className="dx-search">
          {ICO.search}
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("faq.search")} />
          {query && <button onClick={() => setQuery("")} aria-label={t("common.close")}>×</button>}
        </label>
      </Head>
      <section className="dk-wrap dx-faq">
        <aside className="dx-faq__side">
          <nav className="dx-cats">
            <button data-active={cat === "all" && !q} onClick={() => { setCat("all"); setQuery(""); }}>
              <span>{t("faq.all")}</span>
              <small>{items?.length ?? "…"}</small>
            </button>
            {present.map((c) => (
              <button key={c} data-active={cat === c && !q} onClick={() => { setCat(c); setQuery(""); setOpen(null); }}>
                <span>{t(`faq.cats.${c}`)}</span>
                <small>{items?.filter((i) => i.category === c).length}</small>
              </button>
            ))}
          </nav>
          <div className="dx-help">
            <b className="dk-serif">{t("faq.notFound")}</b>
            <p>{t("faq.notFoundHint")}</p>
            <button className="dx-help__btn" onClick={() => window.dispatchEvent(new Event("kms:chat"))}>{ICO.chat}{t("desktop.chatFab")}</button>
            {botUsername && <button className="dx-help__alt" onClick={() => openExternalLink(`https://t.me/${botUsername}`)}>{ICO.tg}{t("faq.writeUs")}</button>}
          </div>
        </aside>
        <div className="dx-faq__list">
          {q && <div className="dx-faq__found">{t("dx.found", { q: query.trim(), n: shown.length })}</div>}
          {items === null ? (
            Array.from({ length: 6 }, (_, i) => <div key={i} className="dk-sk" style={{ height: 70, marginBottom: 10, borderRadius: 16 }} />)
          ) : shown.length === 0 ? (
            <Empty title={q ? t("faq.nothing") : t("faq.empty")} />
          ) : (
            shown.map((i, n) => (
              <div key={i.id} className="dx-qa" data-open={open === i.id}>
                <button className="dx-qa__q" aria-expanded={open === i.id} onClick={() => toggle(i.id)}>
                  <span className="dx-qa__n">{String(n + 1).padStart(2, "0")}</span>
                  <span className="dk-serif">{i.question}</span>
                  <span className="dx-qa__pm">{open === i.id ? ICO.minus : ICO.plus}</span>
                </button>
                {open === i.id && (
                  <div className="dx-qa__a">
                    {(cat === "all" || q) && <span className="dx-qa__cat">{t(`faq.cats.${i.category}`)}</span>}
                    <RichText text={i.answer} />
                    <div className="dx-qa__vote">
                      {voted[i.id] === undefined ? (
                        <>
                          <span>{t("faq.helpful")}</span>
                          <button onClick={() => vote(i.id, true)}>👍 {t("faq.yes")}</button>
                          <button onClick={() => vote(i.id, false)}>{t("faq.no")}</button>
                        </>
                      ) : (
                        <span>{voted[i.id] ? t("faq.thanks") : t("faq.sorry")}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

/* ───────────────────────── Recipes ───────────────────────── */

/** pour-over cone on a carafe */
const POUR = '<path d="M4.5 4h15l-5.2 7.5h-4.6z"/><path d="M12 11.5v2"/><path d="M8 13.5h8l1.6 6.5H6.4z"/><path d="M15 2.5c0 .8-.8.8-.8 1.5"/>';

const fmtTime = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
const totalTime = (r: Recipe) => r.steps.reduce((s, x) => s + x.seconds, 0);

/** Cover of a recipe, or an elegant placeholder with the brewing method when there is no photo. */
function RecipeArt({ r, size }: { r: Recipe; size: number }) {
  const { t } = useI18n();
  return r.coverImage ? (
    <img src={mediaUrl(r.coverImage, size > 400 ? 800 : 400)} alt="" />
  ) : (
    <span className="dx-rart">
      <span className="dx-rart__ico">{WI(POUR, 64)}</span>
      <span className="dk-serif">{t(`recipes.methods.${r.method}`)}</span>
    </span>
  );
}

export function DesktopRecipes() {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const [list, setList] = useState<Recipe[] | null>(null);
  const [method, setMethod] = useState<string>("all");
  useEffect(() => {
    fetchRecipes().then(setList).catch(() => setList([]));
  }, [locale]);
  const methods = useMemo(() => Array.from(new Set((list ?? []).map((r) => r.method))), [list]);
  const shown = (list ?? []).filter((r) => method === "all" || r.method === method);
  return (
    <div className="dx">
      <Head crumbs={[[t("recipes.title")]]} eyebrow={t("recipes.eyebrow")} title={t("recipes.title")} lead={t("recipes.intro")}>
        {methods.length > 1 && (
          <div className="dh-tabs dx-tabs">
            <button data-active={method === "all"} onClick={() => setMethod("all")}>{t("faq.all")}</button>
            {methods.map((m) => <button key={m} data-active={method === m} onClick={() => setMethod(m)}>{t(`recipes.methods.${m}`)}</button>)}
          </div>
        )}
      </Head>
      <section className="dk-wrap">
        {list === null ? (
          <div className="dx-rgrid">{Array.from({ length: 3 }, (_, i) => <div key={i} className="dk-sk" style={{ height: 380, borderRadius: 20 }} />)}</div>
        ) : list.length === 0 ? (
          <Empty title={t("recipes.empty")} hint={t("dx.recipesSoon")} action={<button className="dk-btn" onClick={() => navigate({ name: "catalog", category: "coffee" })}>{t("desktop.allCoffee")} {DI.arrow}</button>} />
        ) : (
          <div className="dx-rgrid">
            {shown.map((r) => (
              <button key={r.id} className="dx-rcard" onClick={() => navigate({ name: "recipe", slug: r.slug })}>
                <span className="dx-rcard__img"><RecipeArt r={r} size={400} /></span>
                <span className="dx-rcard__body">
                  <span className="dk-eyebrow">{t(`recipes.methods.${r.method}`)}</span>
                  <b className="dk-serif">{r.title}</b>
                  {r.description && <span className="dx-rcard__desc">{r.description}</span>}
                  <span className="dx-rcard__meta">
                    <span>{ICO.bean}{r.coffeeGrams} {t("recipes.g")}</span>
                    <span>{ICO.drop}{r.waterMl} {t("recipes.ml")}</span>
                    <span>{ICO.clock}{fmtTime(totalTime(r))}</span>
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/** Brewing timer: steps with their own countdown, auto-advance, pause, "next". */
function useBrew(r: Recipe | null) {
  const [step, setStep] = useState(-1);
  const [left, setLeft] = useState(0);
  const [running, setRunning] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (!running) return;
    timer.current = window.setInterval(() => setLeft((l) => Math.max(0, l - 1)), 1000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [running]);
  const go = useCallback((next: number) => {
    if (!r) return;
    if (next >= r.steps.length) {
      setRunning(false);
      setStep(r.steps.length);
      return;
    }
    setStep(next);
    setLeft(r.steps[next].seconds);
    setRunning(r.steps[next].seconds > 0);
  }, [r]);
  useEffect(() => {
    if (running && left === 0) go(step + 1);
  }, [left, running]); // eslint-disable-line react-hooks/exhaustive-deps
  return { step, left, running, start: () => go(0), next: () => go(step + 1), toggle: () => setRunning((x) => !x), reset: () => { setStep(-1); setRunning(false); } };
}

export function DesktopRecipe({ slug }: { slug: string }) {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const [data, setData] = useState<{ recipe: Recipe; products: Product[] } | null | undefined>(undefined);
  useEffect(() => {
    setData(undefined);
    fetchRecipe(slug).then((d) => setData(d ?? null)).catch(() => setData(null));
  }, [slug, locale]);
  const r = data?.recipe ?? null;
  const brew = useBrew(r);

  if (data === undefined) return <div className="dk-wrap dx-loading"><div className="dk-sk" style={{ height: 460, borderRadius: 24 }} /></div>;
  if (!r) return <div className="dx"><Head crumbs={[[t("recipes.title"), { name: "recipes" }]]} title={t("recipes.notFound")} /><div className="dk-wrap"><Empty title={t("recipes.notFound")} action={<button className="dk-btn" onClick={() => navigate({ name: "recipes" })}>{t("recipes.title")} {DI.arrow}</button>} /></div></div>;

  const finished = brew.step >= r.steps.length;
  const cur = brew.step >= 0 && !finished ? r.steps[brew.step] : null;
  const total = totalTime(r);
  // the ring shows the current step's progress (or the whole recipe before the start)
  const frac = cur ? (cur.seconds ? (cur.seconds - brew.left) / cur.seconds : 1) : finished ? 1 : 0;
  const C = 2 * Math.PI * 92;

  return (
    <div className="dx">
      <Head crumbs={[[t("recipes.title"), { name: "recipes" }], [r.title]]} eyebrow={t(`recipes.methods.${r.method}`)} title={r.title} lead={r.description || undefined} />
      <section className="dk-wrap dx-recipe">
        <div className="dx-recipe__main">
          <div className="dx-recipe__cover"><RecipeArt r={r} size={800} /></div>
          <div className="dx-params">
            <div><span>{ICO.bean}{t("recipes.coffee")}</span><b className="dk-serif">{r.coffeeGrams} {t("recipes.g")}</b></div>
            <div><span>{ICO.drop}{t("recipes.water")}</span><b className="dk-serif">{r.waterMl} {t("recipes.ml")}</b></div>
            <div><span>{ICO.temp}{t("recipes.temp")}</span><b className="dk-serif">{r.tempC ? `${r.tempC}°` : "—"}</b></div>
            <div><span>{ICO.grind}{t("recipes.grind")}</span><b className="dk-serif">{r.grind || "—"}</b></div>
          </div>
          <h2 className="dk-serif dx-h2">{t("dx.recipeSteps")}</h2>
          <ol className="dx-steps">
            {r.steps.map((s, i) => (
              <li key={i} data-state={i < brew.step || finished ? "done" : i === brew.step ? "now" : "todo"}>
                <span className="dx-steps__n">{i < brew.step || finished ? DI.check : i + 1}</span>
                <span className="dx-steps__t">
                  {s.text}
                  {s.pourToMl ? <small>{t("recipes.pourTo", { ml: s.pourToMl })}</small> : null}
                </span>
                {s.seconds > 0 && <span className="dx-steps__s">{fmtTime(s.seconds)}</span>}
              </li>
            ))}
          </ol>
        </div>

        <aside className="dx-timer">
          <div className="dk-eyebrow">{t("dx.recipeTimer")}</div>
          <div className="dx-timer__ring">
            <svg viewBox="0 0 200 200" aria-hidden="true">
              <circle cx="100" cy="100" r="92" className="dx-timer__track" />
              <circle cx="100" cy="100" r="92" className="dx-timer__bar" strokeDasharray={C} strokeDashoffset={C * (1 - frac)} />
            </svg>
            <div className="dx-timer__in">
              {brew.step < 0 ? (
                <><b className="dk-serif">{fmtTime(total)}</b><small>{t("dx.recipeTotal")}</small></>
              ) : finished ? (
                <><b className="dk-serif">☕</b><small>{t("recipes.done")}</small></>
              ) : (
                <><small>{t("recipes.step", { n: brew.step + 1, total: r.steps.length })}</small><b className="dk-serif">{cur!.seconds ? fmtTime(brew.left) : "—"}</b></>
              )}
            </div>
          </div>
          <p className="dx-timer__text">{brew.step < 0 ? t("recipes.ready") : finished ? t("recipes.done") : cur!.text}</p>
          {cur?.pourToMl ? <p className="dx-timer__pour">{t("recipes.pourTo", { ml: cur.pourToMl })}</p> : null}
          <div className="dx-timer__btns">
            {brew.step < 0 ? (
              <button className="dk-btn" onClick={brew.start}>{t("recipes.start")}</button>
            ) : finished ? (
              <button className="dk-btn" onClick={brew.reset}>{t("recipes.again")}</button>
            ) : (
              <>
                {cur!.seconds > 0 && <button className="dk-btn dk-btn--ghost" onClick={brew.toggle}>{brew.running ? t("recipes.pause") : t("recipes.resume")}</button>}
                <button className="dk-btn" onClick={brew.next}>{t("recipes.next")} {DI.arrow}</button>
              </>
            )}
          </div>
        </aside>
      </section>
      {(data?.products.length ?? 0) > 0 && (
        <section className="dk-wrap dx-more">
          <h2 className="dk-serif dx-h2">{t("recipes.forThis")}</h2>
          <div className="dx-pgrid">{data!.products.slice(0, 4).map((p) => <DProductCard key={p.id} product={p} />)}</div>
        </section>
      )}
    </div>
  );
}

/* ───────────────────────── Events ───────────────────────── */

export function DesktopEvents() {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const u = getTelegramUser();
  const [events, setEvents] = useState<ShopEvent[] | null>(null);
  const [mine, setMine] = useState<EventBooking[]>([]);
  const [booking, setBooking] = useState<ShopEvent | null>(null);
  const [form, setForm] = useState({ fullName: [u?.first_name, u?.last_name].filter(Boolean).join(" "), phone: "", seats: 1 });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    fetchEvents().then(setEvents).catch(() => setEvents([]));
    fetchMyBookings().then(setMine).catch(() => setMine([]));
  }, [locale]);
  const confirm = async () => {
    if (!booking) return;
    setBusy(true);
    setError(null);
    try {
      const b = await bookEvent(booking.id, form);
      setBooking(null);
      navigate({ name: "ticket", id: b.id });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="dx">
      <Head crumbs={[[t("events.title")]]} eyebrow={t("events.homeTitle")} title={t("events.title")} lead={t("events.homeText")} />
      <section className="dk-wrap">
        {mine.length > 0 && (
          <div className="dx-mine">
            <span className="dk-eyebrow">{t("events.mine")}</span>
            {mine.map((b) => (
              <button key={b.id} onClick={() => navigate({ name: "ticket", id: b.id })}>
                {ICO.ticket}
                <span><b>{b.event.title}</b><small>{when(b.event.startsAt, locale)}</small></span>
                {DI.arrow}
              </button>
            ))}
          </div>
        )}
        {events === null ? (
          <div className="dk-sk" style={{ height: 260, borderRadius: 22 }} />
        ) : events.length === 0 ? (
          <Empty title={t("events.empty")} />
        ) : (
          <div className="dx-events">
            {events.map((e) => {
              const d = dayMonth(e.startsAt, locale);
              return (
                <article key={e.id} className="dx-event">
                  <div className="dx-event__img">
                    {e.coverImage ? <img src={mediaUrl(e.coverImage, 800)} alt="" /> : <span className="dx-rart"><span className="dx-rart__ico">{WI(PATH.people, 64)}</span></span>}
                    <span className="dx-event__date"><b className="dk-serif">{d.day}</b><small>{d.month}</small></span>
                  </div>
                  <div className="dx-event__body">
                    <span className="dk-eyebrow">{when(e.startsAt, locale)} · {e.durationMin} {t("events.min")}</span>
                    <h3 className="dk-serif">{e.title}</h3>
                    <p>{e.description}</p>
                    <div className="dx-event__meta">
                      <span>{ICO.pin}{e.place}</span>
                      <span data-full={e.seatsLeft <= 0}>{e.seatsLeft > 0 ? t("events.left", { n: e.seatsLeft }) : t("events.full")}</span>
                    </div>
                    <div className="dx-event__row">
                      <b className="dk-serif">{e.priceUzs ? formatUzs(e.priceUzs, locale) : t("events.free")}</b>
                      <button className="dk-btn" disabled={e.seatsLeft <= 0} onClick={() => { setBooking(e); setError(null); setForm((f) => ({ ...f, seats: 1 })); }}>{t("events.book")} {DI.arrow}</button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
      {booking && (
        <div className="dk-modal" role="dialog" aria-modal="true" onClick={() => setBooking(null)}>
          <div className="dk-modal__card dx-book" onClick={(ev) => ev.stopPropagation()}>
            <button className="dk-modal__x" onClick={() => setBooking(null)} aria-label={t("common.close")}>×</button>
            <span className="dk-eyebrow">{t("events.book")}</span>
            <h2 className="dk-serif">{booking.title}</h2>
            <p>{when(booking.startsAt, locale)} · {booking.place}{booking.priceUzs ? ` · ${t("events.payThere", { price: formatUzs(booking.priceUzs, locale) })}` : ""}</p>
            <label className="dx-field"><span>{t("checkout.fullName")}</span><input value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} /></label>
            <label className="dx-field"><span>{t("checkout.phone")}</span><input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+998 90 123 45 67" /></label>
            <div className="dx-seg">
              {[1, 2, 3, 4].map((n) => (
                <button key={n} data-active={form.seats === n} disabled={n > booking.seatsLeft} onClick={() => setForm({ ...form, seats: n })}>{t("events.seats", { n })}</button>
              ))}
            </div>
            {error && <div className="dx-error">{error}</div>}
            <button className="dk-btn dx-book__go" disabled={busy || !form.fullName.trim() || form.phone.replace(/\D/g, "").length < 9} onClick={confirm}>{busy ? t("common.loading") : t("events.confirm")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function DesktopTicket({ id }: { id: string }) {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const [b, setB] = useState<EventBooking | null | undefined>(undefined);
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    fetchMyBookings().then((list) => setB(list.find((x) => x.id === id) ?? null)).catch(() => setB(null));
  }, [id]);
  useEffect(() => {
    if (!b) return;
    import("qrcode")
      .then((m) => m.toDataURL(`KMS-EVENT:${b.code}`, { width: 360, margin: 1, color: { dark: "#173561", light: "#ffffff" } }))
      .then(setQr)
      .catch(() => setQr(null));
  }, [b]);
  return (
    <div className="dx">
      <Head crumbs={[[t("events.title"), { name: "events" }], [t("events.ticket")]]} title={t("events.ticket")} />
      <section className="dk-wrap">
        {b === undefined ? (
          <div className="dk-sk" style={{ height: 260, maxWidth: 760, borderRadius: 24 }} />
        ) : !b ? (
          <Empty title={t("events.noTicket")} action={<button className="dk-btn" onClick={() => navigate({ name: "events" })}>{t("events.title")} {DI.arrow}</button>} />
        ) : (
          <>
            <div className="dx-ticket">
              <div className="dx-ticket__main">
                <span className="dk-eyebrow">KMS ROASTING</span>
                <h2 className="dk-serif">{b.event.title}</h2>
                <div className="dx-ticket__grid">
                  <div><small>{t("dx.when")}</small><b>{when(b.event.startsAt, locale)}</b></div>
                  <div><small>{t("dx.duration")}</small><b>{b.event.durationMin} {t("events.min")}</b></div>
                  <div><small>{t("dx.where")}</small><b>{b.event.place}</b></div>
                  <div><small>{t("dx.guest")}</small><b>{b.fullName} · {t("events.seats", { n: b.seats })}</b></div>
                </div>
              </div>
              <div className="dx-ticket__stub">
                {qr ? <img src={qr} alt="QR" /> : <b className="dx-ticket__code">{b.code}</b>}
                <b>{t("events.showAtDoor")}</b>
                <small>{t("events.reminder")}</small>
              </div>
            </div>
            <button className="dx-cancel" onClick={async () => { await cancelBooking(b.id).catch(() => {}); navigate({ name: "events" }); }}>{t("events.cancel")}</button>
          </>
        )}
      </section>
    </div>
  );
}

/* ───────────────────────── Reviews ───────────────────────── */

const PAGE = 24;

export function DesktopReviews({ slug }: { slug?: string }) {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const { products } = useCatalog();
  const [items, setItems] = useState<Review[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState<string | null>(null);
  const product = slug ? products.find((p) => p.slug === slug) : undefined;
  const load = useCallback(async (offset: number) => {
    setLoading(true);
    try {
      const res = slug ? await fetchReviews(slug, PAGE, offset) : await fetchLatestReviews(PAGE, offset);
      setItems((prev) => (offset === 0 ? res.items : [...prev, ...res.items]));
      setTotal(res.total);
    } catch {
      setTotal((prev) => prev ?? 0);
    } finally {
      setLoading(false);
    }
  }, [slug]);
  useEffect(() => {
    load(0);
  }, [load]);
  const avg = items.length ? items.reduce((s, r) => s + r.rating, 0) / items.length : 0;
  const rating = product?.rating ?? avg;
  const fmtDate = (iso?: string) => (iso ? new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "ru-RU", { day: "numeric", month: "long", year: "numeric" }).format(new Date(iso)) : "");
  return (
    <div className="dx">
      <Head
        crumbs={product ? [[product.name, { name: "product", slug: product.slug }], [t("reviews.allTitle")]] : [[t("reviews.allTitle")]]}
        eyebrow={product ? product.name : t("dx.reviewsEyebrow")}
        title={t("reviews.allTitle")}
      >
        {total !== null && total > 0 && (
          <div className="dx-rating">
            <b className="dk-serif">{rating.toFixed(1)}</b>
            <span className="dx-stars">{"★".repeat(Math.round(rating))}<i>{"★".repeat(5 - Math.round(rating))}</i></span>
            <small>{reviewsText(total, locale, t)}</small>
          </div>
        )}
      </Head>
      <section className="dk-wrap">
        {total === null ? (
          <div className="dx-reviews">{Array.from({ length: 6 }, (_, i) => <div key={i} className="dk-sk" style={{ height: 160, borderRadius: 18 }} />)}</div>
        ) : items.length === 0 ? (
          <Empty title={t("product.noReviews")} />
        ) : (
          <>
            <div className="dx-reviews">
              {items.map((r) => (
                <article key={r.id} className="dx-review">
                  <div className="dx-review__top">
                    <span className="dx-review__ava">{r.authorName.charAt(0).toUpperCase()}</span>
                    <span><b>{r.authorName}</b><small>{fmtDate(r.createdAt)}</small></span>
                    <span className="dx-stars">{"★".repeat(r.rating)}<i>{"★".repeat(5 - r.rating)}</i></span>
                  </div>
                  {!slug && r.product && <button className="dx-review__prod" onClick={() => navigate({ name: "product", slug: r.product!.slug })}>{r.product.name} {DI.arrow}</button>}
                  {r.text && <p>{r.text}</p>}
                  {!!r.photos?.length && (
                    <div className="dx-review__photos">
                      {r.photos.map((p) => <button key={p} onClick={() => setZoom(p)}><img src={mediaUrl(p, 200)} alt="" loading="lazy" /></button>)}
                    </div>
                  )}
                </article>
              ))}
            </div>
            {items.length < total && (
              <div className="dx-center"><button className="dk-btn dk-btn--ghost" disabled={loading} onClick={() => load(items.length)}>{loading ? t("common.loading") : t("reviews.showMore")}</button></div>
            )}
          </>
        )}
      </section>
      {zoom && (
        <div className="dk-modal" role="dialog" aria-modal="true" onClick={() => setZoom(null)}>
          <img className="dx-zoom" src={mediaUrl(zoom)} alt="" />
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Compare ───────────────────────── */

export function DesktopCompare() {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const { getProduct } = useCatalog();
  const { addItem } = useCart();
  const compare = useCompare();
  const products = compare.ids.map((id) => getProduct(id)).filter((p): p is NonNullable<typeof p> => !!p);
  if (products.length < 2) {
    return (
      <div className="dx">
        <Head crumbs={[[t("compare.title")]]} title={t("compare.title")} />
        <div className="dk-wrap"><Empty title={t("compare.emptyTitle")} hint={t("compare.emptyHint")} action={<button className="dk-btn" onClick={() => navigate({ name: "catalog", category: "coffee" })}>{t("desktop.allCoffee")} {DI.arrow}</button>} /></div>
      </div>
    );
  }
  const keys: string[] = Array.from(new Set<string>(products.flatMap((p) => Object.keys(p.attributes ?? {}))));
  const cols = { gridTemplateColumns: `220px repeat(${products.length}, minmax(0, 1fr))` };
  const row = (label: string, cells: React.ReactNode[]) => (
    <div className="dx-cmp__row" style={cols} key={label}>
      <span className="dx-cmp__label">{label}</span>
      {cells.map((c, i) => <span key={i}>{c}</span>)}
    </div>
  );
  return (
    <div className="dx">
      <Head crumbs={[[t("compare.title")]]} title={t("compare.title")}>
        <button className="dx-link" onClick={() => compare.ids.forEach((id) => compare.remove(id))}>{t("compare.clear")}</button>
      </Head>
      <section className="dk-wrap">
        <div className="dx-cmp">
          <div className="dx-cmp__row dx-cmp__head" style={cols}>
            <span />
            {products.map((p) => {
              const v = p.variants.find((x) => x.stock > 0);
              return (
                <div key={p.id} className="dx-cmp__prod">
                  <button className="dx-cmp__img" onClick={() => navigate({ name: "product", slug: p.slug })}>{p.images[0] && <img src={mediaUrl(p.images[0], 400)} alt={p.name} />}</button>
                  <b className="dk-serif">{p.name}</b>
                  <span className="dx-cmp__price">{p.variants[0].priceUzs > 0 ? formatUzs(p.variants[0].priceUzs, locale) : t("catalog.onRequest")} · {p.variants[0].label}</span>
                  <div className="dx-cmp__acts">
                    <button className="dk-btn" disabled={!v || p.orderMode !== "buy_now"} onClick={(e) => { if (!v) return; addItem(p.id, v.id); flyToCart(e.currentTarget); }}>{v ? t("product.addToCart") : t("product.outOfStockShort")}</button>
                    <button className="dx-link" onClick={() => compare.remove(p.id)}>{t("compare.remove")}</button>
                  </div>
                </div>
              );
            })}
          </div>
          {row(t("compare.rating"), products.map((p) => (p.rating ? <span className="dx-stars">★ {p.rating.toFixed(1)} <small>· {p.reviewsCount ?? 0}</small></span> : "—")))}
          {keys.map((k) => row(k, products.map((p) => p.attributes?.[k] ?? "—")))}
          {row(t("compare.about"), products.map((p) => <span className="dx-cmp__about">{p.shortDescription}</span>))}
        </div>
      </section>
    </div>
  );
}

/* ───────────────────────── Wishlist ───────────────────────── */

export function DesktopWishlist({ code }: { code: string }) {
  const { t, locale } = useI18n();
  const { navigate } = useRouter();
  const [data, setData] = useState<{ ownerName: string | null; products: Product[] } | null | undefined>(undefined);
  useEffect(() => {
    fetchWishlist(code).then(setData).catch(() => setData(null));
  }, [code, locale]);
  const title = data?.ownerName ? t("wishlist.from", { name: data.ownerName }) : t("wishlist.title");
  return (
    <div className="dx">
      <Head crumbs={[[t("wishlist.title")]]} eyebrow={t("wishlist.eyebrow")} title={title} lead={data && data.products.length ? t("wishlist.hint") : undefined} />
      <section className="dk-wrap">
        {data === undefined ? (
          <div className="dx-pgrid">{Array.from({ length: 4 }, (_, i) => <DSkeletonCard key={i} />)}</div>
        ) : !data || data.products.length === 0 ? (
          <Empty title={t("wishlist.notFound")} action={<button className="dk-btn" onClick={() => navigate({ name: "home" })}>{t("common.toHome")} {DI.arrow}</button>} />
        ) : (
          <div className="dx-pgrid">{data.products.map((p) => <DProductCard key={p.id} product={p} />)}</div>
        )}
      </section>
    </div>
  );
}

/* ───────────────────────── Gift ───────────────────────── */

export function DesktopGift({ code }: { code: string }) {
  const { t } = useI18n();
  const { navigate } = useRouter();
  const u = getTelegramUser();
  const [gift, setGift] = useState<GiftView | null | undefined>(undefined);
  const [method, setMethod] = useState<"pickup" | "yandex">("yandex");
  const [fullName, setFullName] = useState([u?.first_name, u?.last_name].filter(Boolean).join(" "));
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [comment, setComment] = useState("");
  const [slot, setSlot] = useState<{ date?: string; slot?: string }>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => fetchGift(code).then(setGift).catch(() => setGift(null)), [code]);
  useEffect(() => {
    load();
  }, [load]);
  const claim = async () => {
    if (!fullName.trim() || phone.replace(/\D/g, "").length < 9 || (method === "yandex" && !address.trim())) return setError(t("gift.fillAll"));
    setBusy(true);
    setError(null);
    try {
      await claimGift(code, { fullName: fullName.trim(), phone: phone.trim(), deliveryMethod: method, address: method === "yandex" ? address.trim() : undefined, comment: comment.trim() || undefined, deliveryDate: slot.slot ? slot.date : undefined, deliverySlot: slot.slot });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  };
  if (gift === undefined) return <div className="dk-wrap dx-loading"><div className="dk-sk" style={{ height: 420, borderRadius: 24 }} /></div>;
  if (!gift) return <div className="dx"><Head crumbs={[[t("gift.title")]]} title={t("gift.title")} /><div className="dk-wrap"><Empty title={t("gift.notFound")} action={<button className="dk-btn" onClick={() => navigate({ name: "home" })}>{t("common.toHome")} {DI.arrow}</button>} /></div></div>;
  return (
    <div className="dx">
      <Head crumbs={[[t("gift.title")]]} title={t("gift.title")} />
      <section className="dk-wrap dx-gift">
        <div className="dx-gift__card">
          <span className="dx-gift__ico">{ICO.gift}</span>
          <span className="dk-eyebrow">{t("gift.eyebrow")}</span>
          <h2 className="dk-serif">{gift.fromName ? t("gift.fromName", { name: gift.fromName }) : t("gift.forYou")}</h2>
          {gift.message && <p className="dx-gift__msg dk-serif">«{gift.message}»</p>}
          <ul>
            {gift.items.map((i, k) => (
              <li key={k}><span>{i.productName}{i.variantLabel !== "Стандарт" ? ` · ${i.variantLabel}` : ""}</span><b>× {i.quantity}</b></li>
            ))}
          </ul>
        </div>
        <div className="dx-gift__form">
          {!gift.paid ? (
            <div className="dx-note">{t("gift.notPaid")}</div>
          ) : gift.claimed ? (
            <div className="dx-note dx-note--ok"><b>◆ {t("gift.claimedTitle")}</b><span>{gift.deliveryMethod === "pickup" ? t("gift.claimedPickup") : t("gift.claimedDelivery", { when: gift.deliverySlot ?? "" })}</span></div>
          ) : gift.isBuyer ? (
            <div className="dx-note">{t("gift.buyerView")}</div>
          ) : (
            <>
              <h3 className="dk-serif">{t("gift.how")}</h3>
              <div className="dx-seg">
                <button data-active={method === "yandex"} onClick={() => setMethod("yandex")}>{t("gift.delivery")}</button>
                <button data-active={method === "pickup"} onClick={() => setMethod("pickup")}>{t("gift.pickup")}</button>
              </div>
              <div className="dx-two">
                <label className="dx-field"><span>{t("checkout.fullName")}</span><input value={fullName} onChange={(e) => setFullName(e.target.value)} /></label>
                <label className="dx-field"><span>{t("checkout.phone")}</span><input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+998 90 123 45 67" /></label>
              </div>
              {method === "yandex" ? (
                <>
                  <label className="dx-field"><span>{t("checkout.address")}</span><input value={address} onChange={(e) => setAddress(e.target.value)} /></label>
                  <DeliveryPicker value={slot} onChange={setSlot} />
                </>
              ) : (
                <div className="dx-note">{t("checkout.pickupAddress")} · {t("checkout.pickupHours")}</div>
              )}
              <label className="dx-field"><span>{t("checkout.comment")}</span><textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} /></label>
              {error && <div className="dx-error">{error}</div>}
              <button className="dk-btn dx-gift__go" disabled={busy} onClick={claim}>{busy ? t("common.loading") : t("gift.claim")} {DI.arrow}</button>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

/* ───────────────────────── Subscription link ───────────────────────── */

/** Opened from the bot reminder: puts the subscription into the cart and goes to checkout. */
export function DesktopSubscription({ id }: { id: string }) {
  const { t } = useI18n();
  const { navigate } = useRouter();
  const { addItem } = useCart();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    fetchSubscription(id)
      .then((s) => {
        addItem(s.productId, s.variantId, s.quantity);
        navigate({ name: "checkout" });
      })
      .catch(() => setFailed(true));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="dx">
      <Head crumbs={[[t("subscription.listTitle")]]} title={t("subscription.title")} />
      <div className="dk-wrap">
        {failed ? <Empty title={t("common.error")} action={<button className="dk-btn" onClick={() => navigate({ name: "catalog" })}>{t("desktop.allCatalog")} {DI.arrow}</button>} /> : <div className="dx-empty"><p>{t("common.loading")}</p></div>}
      </div>
    </div>
  );
}
